<script>
    import toast from "svelte-french-toast";
    import { getSelectableMunicipalities } from "../../../services/jurisdictionService";
    import { provinces } from "../../../utils/provinces";

    export let groupId = undefined;
    export let year = undefined;
    export let value = []; // array of { label, value: cityId } — bound by the parent form

    let rows = []; // [{ id, name, province, selectable, assignedGroupName }]
    let isLoading = false;
    let filterText = "";
    let provinceFilter = ""; // selected province code (provinces[].value), "" = all
    let loadedKey; // guards against refetching for the same group/year
    let showSelected = false; // toggles the pills of selected comuni

    // A comune is checked when it is in the bound selection
    $: selectedIds = new Set(value.map((v) => v.value));

    // Only offer the provinces actually present among the loaded comuni,
    // keeping provinces.js labels/order. row.province may hold either the
    // code ("MI") or a full name ("Milano"), so match on both.
    $: presentProvinces = new Set(
        rows.map((r) => (r.province ?? "").toString().trim().toLowerCase()).filter(Boolean)
    );
    $: provinceOptions = provinces.filter(
        (p) =>
            presentProvinces.has(p.value.toLowerCase()) ||
            presentProvinces.has(p.label.replace(/\s*\(.*\)\s*$/, "").trim().toLowerCase())
    );

    // Selected province, tolerating either the code ("MI") or a full name
    // ("Milano") coming back from the backend in row.province
    $: selectedProvince = provinceFilter ? provinces.find((p) => p.value === provinceFilter) : undefined;
    $: provinceCode = selectedProvince?.value.toLowerCase();
    $: provinceName = selectedProvince?.label.replace(/\s*\(.*\)\s*$/, "").trim().toLowerCase();

    $: filteredRows = rows.filter((r) => {
        if (filterText && !r.name.toLowerCase().includes(filterText.trim().toLowerCase())) {
            return false;
        }
        if (selectedProvince) {
            const rowProvince = (r.province ?? "").toString().trim().toLowerCase();
            if (rowProvince !== provinceCode && rowProvince !== provinceName) {
                return false;
            }
        }
        return true;
    });

    // Select-all only ever operates on the selectable, currently-visible comuni
    $: selectableFiltered = filteredRows.filter((r) => r.selectable);
    $: allSelected = selectableFiltered.length > 0 && selectableFiltered.every((r) => selectedIds.has(r.id));

    $: key = groupId ? `${groupId}:${year ?? ""}` : undefined;
    $: if (key && key !== loadedKey) {
        loadedKey = key;
        loadMunicipalities();
    }

    async function loadMunicipalities() {
        isLoading = true;
        try {
            const resp = await getSelectableMunicipalities(groupId, year);
            rows = resp?.data ?? [];
        } catch (err) {
            toast.error(`Errore durante il caricamento dei comuni, errore ${err.toString()}`);
            rows = [];
        } finally {
            isLoading = false;
        }
    }

    function toggle(row) {
        if (!row.selectable) {
            return;
        }

        value = selectedIds.has(row.id)
            ? value.filter((v) => v.value !== row.id)
            : [...value, { label: row.name, value: row.id }];
    }

    function removeMunicipality(item) {
        value = value.filter((v) => v.value !== item.value);
    }

    function toggleSelectAll() {
        if (allSelected) {
            const toRemove = new Set(selectableFiltered.map((r) => r.id));
            value = value.filter((v) => !toRemove.has(v.value));
        } else {
            const existing = new Set(value.map((v) => v.value));
            const toAdd = selectableFiltered
                .filter((r) => !existing.has(r.id))
                .map((r) => ({ label: r.name, value: r.id }));
            value = [...value, ...toAdd];
        }
    }
</script>

{#if !groupId}
    <div class="flex items-center h-11 input-box-disabled pl-4 text-blueGray-400">
        Seleziona prima una federazione
    </div>
{:else}
    <div class="flex flex-col gap-3">
        <div class="flex flex-col gap-2">
            <button
                type="button"
                class="flex items-center gap-2 w-fit disabled:cursor-default"
                on:click={() => (showSelected = !showSelected)}
                disabled={value.length === 0}
            >
                <span class="whitespace-nowrap font-semibold text-lg text-lightBlue-600">
                    {selectedIds.size} comuni selezionati
                </span>
                {#if value.length}
                    <i class="fa {showSelected ? 'fa-chevron-up' : 'fa-chevron-down'} text-lightBlue-600 text-sm"></i>
                {/if}
            </button>

            {#if showSelected && value.length}
                <div class="flex flex-wrap gap-2 p-3 rounded border bg-blueGray-50">
                    {#each value as item (item.value)}
                        <span class="inline-flex items-center gap-2 pl-3 pr-2 py-1 rounded-full bg-blueGray-200 text-blueGray-700 text-sm">
                            {item.label}
                            <i
                                class="fa fa-times cursor-pointer hover:text-red-500"
                                on:click={() => removeMunicipality(item)}
                            ></i>
                        </span>
                    {/each}
                </div>
            {/if}
        </div>

        <div class="flex flex-col lg:flex-row gap-2">
            <input
                class="h-11 px-4 w-full border-gray-300"
                type="text"
                placeholder="Filtra per nome del comune"
                bind:value={filterText}
            />

            <select
                class="h-11 px-4 w-full lg:w-64 border-gray-300"
                bind:value={provinceFilter}
            >
                <option value="">Tutte le province</option>
                {#each provinceOptions as p (p.value)}
                    <option value={p.value}>{p.label}</option>
                {/each}
            </select>
        </div>

        <div class="table-area border rounded">
            {#if isLoading}
                <div class="flex items-center justify-center h-32 text-blueGray-400">Caricamento…</div>
            {:else if !rows.length}
                <div class="flex items-center justify-center h-32 text-blueGray-400">Nessun comune nella regione</div>
            {:else if !filteredRows.length}
                <div class="flex items-center justify-center h-32 text-blueGray-400">Nessun comune corrisponde al filtro</div>
            {:else}
                <table class="w-full text-left">
                    <thead class="sticky top-0 z-10">
                        <tr class="border-b">
                            <th class="w-12 py-2 px-3 bg-blueGray-100">
                                <input
                                    type="checkbox"
                                    class="cursor-pointer w-5 h-5"
                                    checked={allSelected}
                                    disabled={selectableFiltered.length === 0}
                                    on:change={toggleSelectAll}
                                />
                            </th>
                            <th class="py-2 px-3 font-semibold bg-blueGray-100">Comune</th>
                            <th class="py-2 px-3 font-semibold bg-blueGray-100">Provincia</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each filteredRows as row (row.id)}
                            <tr
                                class="border-b {row.selectable ? 'hover:bg-blueGray-50 cursor-pointer' : 'not-selectable'}"
                                on:click={() => toggle(row)}
                            >
                                <td class="py-2 px-3">
                                    <input
                                        type="checkbox"
                                        class="w-5 h-5 {row.selectable ? 'cursor-pointer' : 'cursor-not-allowed'}"
                                        checked={selectedIds.has(row.id)}
                                        disabled={!row.selectable}
                                        on:click|stopPropagation
                                        on:change={() => toggle(row)}
                                    />
                                </td>
                                <td class="py-2 px-3">
                                    <span class="font-semibold">{row.name}</span>
                                    {#if !row.selectable && row.assignedGroupName}
                                        <span class="ml-2 text-blueGray-400 text-sm">Assegnato a {row.assignedGroupName}</span>
                                    {/if}
                                </td>
                                <td class="py-2 px-3">{row.province}</td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            {/if}
        </div>
    </div>
{/if}

<style>
    .table-area {
        max-height: 24rem;
        overflow-y: auto;
        width: 100%;
    }

    .not-selectable {
        opacity: 0.55;
        cursor: not-allowed;
    }
</style>
